import '@/chassis/ui/assets/css/vendors/dropzone.css';
import React, { useEffect, useRef } from 'react';
import DropzoneJs from '@/chassis/ui/components/data-input/Dropzone/dropzone-lib';
import type { DropzoneOptions } from 'dropzone';

DropzoneJs.autoDiscover = false;

type DropzoneInstance = InstanceType<typeof DropzoneJs>;
export type DropzoneElement = HTMLDivElement & { dropzone: DropzoneInstance };

export interface DropzoneProps extends React.PropsWithChildren, React.ComponentPropsWithoutRef<'div'> {
    options: DropzoneOptions;
    getRef: (el: DropzoneElement) => void;
}

function Dropzone({ options = {}, getRef = () => {}, children, ...computedProps }: DropzoneProps) {
    const elementRef = useRef<HTMLDivElement | null>(null);
    const dropzoneRef = useRef<DropzoneInstance | null>(null);
    const optionsRef = useRef(options);

    optionsRef.current = options;

    useEffect(() => {
        const element = elementRef.current as DropzoneElement | null;
        if (!element) {
            return;
        }

        if (!dropzoneRef.current) {
            const instance = new DropzoneJs(element, optionsRef.current);
            dropzoneRef.current = instance;
            element.dropzone = instance;
        }

        const instance = dropzoneRef.current;
        return () => {
            if (instance && typeof instance.destroy === 'function') {
                instance.destroy();
            }
            dropzoneRef.current = null;
            delete (element as { dropzone?: DropzoneInstance }).dropzone;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const element = elementRef.current as DropzoneElement | null;
        if (element?.dropzone) {
            getRef(element);
        }
    }, [getRef]);

    return (
        <div
            {...computedProps}
            ref={elementRef}
            className="[&.dropzone]:border-2 [&.dropzone]:border-dashed dropzone [&.dropzone]:border-slate-300/70 [&.dropzone]:bg-slate-50 [&.dropzone]:cursor-pointer [&.dropzone]:dark:bg-darkmode-600 [&.dropzone]:dark:border-white/5"
        >
            <div className="dz-message">{children}</div>
        </div>
    );
}

export default Dropzone;
